/**
 * A coding agent's subscription usage on one machine (state.scheduler.usage, module
 * 'agent-usage'), compact like the claude.ai usage panel: one row per window with its label, a
 * thin bar, the percent used and when it resets. A refused agent says so first. Routing weighs
 * the same numbers (agent-score.ts capacity), so what you see is what placement sees.
 */
import { agentKindLabel, liveWindows, usageRefused } from '@orgus/protocol';
import type { AgentUsage } from '@orgus/protocol';
import { ProgressBar, formatClock, formatDate, formatPercent, formatRelative } from '@orgus/ui';
import { TriangleAlert } from '@orgus/ui/icons';

const DAY_MS = 20 * 60 * 60_000;
const SOURCE: Record<AgentUsage['source'], string> = { probe: 'its /usage check', stream: 'a run Orgus started', statusline: 'a session status line' };

/** "1:00 PM" within the day, else "Oct 22, 1:00 PM". */
export function resetLabel(at: number, now: number): string {
  return at - now < DAY_MS ? formatClock(at) : `${formatDate(at, now)}, ${formatClock(at)}`;
}

export function AgentUsageRows({ usage, machineName, now }: { usage: AgentUsage; machineName: string | null; now: number }) {
  const windows = liveWindows(usage, now);
  const rejected = usageRefused(usage, now);
  if (!windows.length && !rejected) return null;
  const label = agentKindLabel(usage.agentKind);
  return (
    <div className="flex flex-col gap-1 text-caption" title={`Read ${formatRelative(usage.at, now)} from ${SOURCE[usage.source]}`} aria-label={`${label} usage${machineName ? ` on ${machineName}` : ''}`}>
      {machineName && <span className="text-fg-subtle">{machineName}</span>}
      {rejected && (
        <span className="flex items-center gap-1 text-status-attention">
          <TriangleAlert className="size-3" aria-hidden />
          Usage limit reached{usage.resetsAt ? `, resets ${resetLabel(usage.resetsAt, now)}` : ''}
        </span>
      )}
      {windows.map((w) => {
        const high = w.severity !== 'normal' || (w.percent ?? 0) >= 80;
        return (
          <div key={`${w.kind}:${w.scope ?? ''}`} className="flex items-center gap-2">
            <span className="w-32 shrink-0 truncate text-fg-muted">{w.label}</span>
            {w.percent !== null ? (
              <>
                <ProgressBar value={w.percent / 100} label={`${w.label}: ${formatPercent(w.percent / 100)} used`} tone={high ? 'attention' : 'neutral'} className="w-24 shrink-0" />
                <span className={high ? 'w-16 shrink-0 text-fg num' : 'w-16 shrink-0 text-fg-muted num'}>{formatPercent(w.percent / 100)} used</span>
              </>
            ) : (
              <span className="w-40 shrink-0 text-fg-subtle">Not reported</span>
            )}
            {w.resetsAt !== null && <span className="truncate text-fg-subtle">Resets {resetLabel(w.resetsAt, now)}</span>}
          </div>
        );
      })}
    </div>
  );
}
